import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { IonSpinner } from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import {
  CPSConfigFunctionnalityGraphiques,
  HistoryChartModeEnum,
  HistoryGraphModeEnum,
  HistoryGraphYModeEnum,
} from '../../../../IProduct';
import { DateInterval } from '../../../../../../../components/input-dateinterval/input-dateinterval.component';
import {
  DataProductType,
  PoolDataApi,
} from '../../../../../../../apis/poolData.api';
import {
  PoolEventsApi,
  PoolProductEvent,
} from '../../../../../../../apis/poolEvent.api';
import { Chart, registerables } from 'chart.js';
import {
  convertSecondsToHM,
  convertSecondsToTime,
  getModeFromStartAndEndDate,
} from '../../../../../helper/convertSecondsToTime';
import moment from 'moment/moment';
import {
  brandOverride,
  getProductByModelIdentifier,
  isConnect,
  isMaster,
} from '../../../../../ECoSystemsHelper';
import { getTemperature } from '../../../../../helper/temperature';
import { AppComponent } from '../../../../../../../app.component';

@Component({
  selector: 'app-p00037-function-history-graph-component',
  templateUrl: './p00037-function-history-graph-component.component.html',
  styleUrls: ['./p00037-function-history-graph-component.component.scss'],
  imports: [IonSpinner, TranslatePipe],
})
export class P00037FunctionHistoryGraphComponentComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  @Input() poolId: string | null = null;
  @Input() config: CPSConfigFunctionnalityGraphiques | null = null;
  @Input() dateInterval: DateInterval | null = null;

  private poolDataApi = inject(PoolDataApi);
  private poolEventApi = inject(PoolEventsApi);

  @ViewChild('chart', { static: false })
  chartRef!: ElementRef<HTMLCanvasElement>;
  private chart: any = null;

  public isLoading = true;
  public totalValue: string | null = null;

  constructor() {
    Chart.register(...registerables);
  }

  ngOnInit() {
    console.log('todo', this.config, this.dateInterval);
  }

  calculTotal(datas: any = []) {
    let res = 0;

    for (let i = 0; i < datas.length; i++) {
      res += datas[i];
    }

    this.totalValue = convertSecondsToTime(res);
  }

  refreshDatas() {
    this.isLoading = true;

    const productClass = getProductByModelIdentifier(
      this.config?.modelIdentifier,
    );

    let productType = DataProductType.PRODUCT;

    if (isMaster(productClass!)) {
      productType = DataProductType.MASTER;
    } else if (isConnect(productClass!)) {
      productType = DataProductType.CONNECT;
    }

    if (this.config!.mode == HistoryGraphModeEnum.DATA) {
      this.poolDataApi
        .getPoolProductDatasProperties(
          this.poolId!,
          productType,
          productClass!.modelIdentifier!,
          this.config!.customGraph.properties!,
          this.config!.aggregate!,
          Math.floor(this.dateInterval!.startTimestamp! / 1000),
          Math.floor(this.dateInterval!.endTimestamp! / 1000),
        )
        .then((productDatas) => {
          this.createBarChart(productDatas);

          this.isLoading = false;
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }

  ngAfterViewInit() {
    if (this.chartRef && this.chartRef.nativeElement) {
      this.refreshDatas();
    }
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Vérifier si dateInterval a changé
    if (changes['dateInterval'] && !changes['dateInterval'].firstChange) {
      if (this.chartRef && this.chartRef.nativeElement) {
        this.refreshDatas();
      }
    }
  }

  private createBarChart(datas: any = []) {
    const ctx = this.chartRef.nativeElement.getContext('2d');
    if (!ctx) return;

    let res = [];
    let max = 3600;

    for (let i = 0; i < this.config!.customGraph.properties.length; i++) {
      let total = 0;
      const prop = this.config!.customGraph.properties[i];

      if (!datas[prop]) {
        continue;
      }

      let d = datas[prop];

      for (let j = 0; j < d.length; j++) {
        total += d[j].value;
      }

      if (max < total) {
        max = total;
      }

      res.push(total);
    }

    this.calculTotal(res);

    if (this.chart) {
      this.chart.destroy();
    }

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.config?.customGraph.axesX,
        datasets: [
          {
            data: res,
            backgroundColor:
              AppComponent.currentBrand?.getMainColor() ?? '#f39200',
            borderColor: AppComponent.currentBrand?.getMainColor() ?? '#f39200',
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        animation: false,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const value = context.parsed.y;
                return convertSecondsToTime(value) as any;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,

            min: 0,
            max: Math.max(...[...res, max]),

            grid: {
              display: true,
              color: '#C5CBD8',
            },

            ticks: {
              maxRotation: 0, // Désactiver la rotation
              minRotation: 0, // Désactiver la rotation
              stepSize: 1800,
              autoSkip: false,
              callback: (value: any) => {
                return convertSecondsToHM(value);
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            ticks: {
              maxRotation: 0, // Désactiver la rotation
              minRotation: 0, // Désactiver la rotation
              autoSkip: true, // Activer le masquage automatique
              autoSkipPadding: 10, // Padding entre les labels visibles
            },
          },
        },
      },
    });
  }

  convertValue(value: number) {
    return value;
  }

  protected readonly getProductByModelIdentifier = getProductByModelIdentifier;
  protected readonly brandOverride = brandOverride;
}
